import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NbDialogRef } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import {
  InvoiceImportSubAdviceEntry,
  WholesalerSubstitution,
  WholesalerSubstitutionStrategy,
} from 'src/app/models/invoice-import.model';

export interface SubstitutionMappingDialogResult {
  strategy: WholesalerSubstitutionStrategy;
  toSupplierItemNumber?: string | null;
  toName?: string | null;
  confirmed?: boolean;
}

@Component({
  selector: 'app-substitution-mapping-dialog',
  templateUrl: './substitution-mapping-dialog.component.html',
  styleUrls: ['./substitution-mapping-dialog.component.scss'],
})
export class SubstitutionMappingDialogComponent implements OnInit {
  /** Sub-advice row (create / edit from import detail). */
  @Input() entry?: InvoiceImportSubAdviceEntry;
  /** Edit an existing store mapping (settings list or PATCH flow). */
  @Input() existingSubstitution?: WholesalerSubstitution;

  form!: FormGroup;
  readonly strategies: WholesalerSubstitutionStrategy[] = ['permanent', 'one_delivery', 'until_restock'];

  constructor(
    protected ref: NbDialogRef<SubstitutionMappingDialogComponent>,
    private fb: FormBuilder,
    private translate: TranslateService
  ) {}

  get isEditMode(): boolean {
    return !!this.existingSubstitution?._id || !!this.entry?.mapping?._id;
  }

  get requiresConfirmation(): boolean {
    if (this.isEditMode) {
      return false;
    }
    return !!this.entry?.requiresConfirmation;
  }

  get noticeKind(): string | undefined {
    return this.entry?.noticeKind || this.existingSubstitution?.noticeKind || undefined;
  }

  ngOnInit(): void {
    const sub = this.existingSubstitution;
    const entry = this.entry;
    const editMode = this.isEditMode;

    let strategy: WholesalerSubstitutionStrategy | null = null;
    let toSupplierItemNumber = '';
    let toName = '';

    if (editMode) {
      strategy = (sub?.strategy ?? entry?.mapping?.strategy ?? null) as WholesalerSubstitutionStrategy | null;
      toSupplierItemNumber =
        sub?.toSupplierItemNumber ?? entry?.mapping?.toSupplierItemNumber ?? '';
      toName = sub?.toName ?? entry?.mapping?.toName ?? '';
    } else {
      strategy = entry?.suggestedStrategy ?? null;
      toSupplierItemNumber = entry?.replacementCode ?? '';
      toName = entry?.replacementName ?? '';
    }

    this.form = this.fb.group({
      toSupplierItemNumber: [toSupplierItemNumber ?? ''],
      toName: [toName ?? ''],
      strategy: [strategy, Validators.required],
      confirmed: [false],
    });
    if (this.requiresConfirmation) {
      this.form.get('confirmed')?.setValidators([Validators.requiredTrue]);
    }
  }

  fromCodeDisplay(): string {
    return this.existingSubstitution?.fromSupplierItemNumber || this.entry?.code || '—';
  }

  fromNameDisplay(): string {
    return this.existingSubstitution?.fromName || this.entry?.name || '—';
  }

  statusDisplay(): string {
    const text = (this.entry?.statusText || this.existingSubstitution?.statusText || '').trim();
    return text || '—';
  }

  strategyLabel(strategy: WholesalerSubstitutionStrategy): string {
    return this.translate.instant(`INVOICE_IMPORTS.SUB.STRATEGY_${strategy.toUpperCase()}`);
  }

  confirmWarningKey(): string {
    if (this.noticeKind === 'discontinued') {
      return 'INVOICE_IMPORTS.SUB.SAVE_CONFIRM_DISCONTINUED';
    }
    return 'INVOICE_IMPORTS.SUB.SAVE_CONFIRM_WARNING';
  }

  cancel(): void {
    this.ref.close(null);
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const raw = this.form.getRawValue();
    const trim = (v: unknown) => String(v ?? '').trim();
    const result: SubstitutionMappingDialogResult = {
      strategy: raw.strategy as WholesalerSubstitutionStrategy,
    };
    if (this.isEditMode) {
      result.toSupplierItemNumber = trim(raw.toSupplierItemNumber) || null;
      result.toName = trim(raw.toName) || null;
    } else {
      const toNum = trim(raw.toSupplierItemNumber);
      const toNm = trim(raw.toName);
      if (toNum) {
        result.toSupplierItemNumber = toNum;
      }
      if (toNm) {
        result.toName = toNm;
      }
      if (this.requiresConfirmation) {
        result.confirmed = !!raw.confirmed;
      }
    }
    this.ref.close(result);
  }
}
